import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of, throwError } from "rxjs";
import { map, tap, catchError, delay, finalize, shareReplay } from "rxjs/operators";
import { withErrorHandler } from './error-handler.util';
import { environment } from "../../environments";
import { SearchTierResponse, Tier, TierLicense } from "../interfaces/v2/tier";
import { GrindRestriction } from "../interfaces/v2/grind-restriction";
import {
  nextGrindRestrictionsForceRefreshEpoch,
  shouldCommitGrindRestrictionsCache,
} from "../views/team-global-settings/grind-restrictions";

@Injectable({
  providedIn: "root",
})
export class TierService {
  baseURL = environment.API_URL + "v2/tier/";
  baseURLMisc = environment.API_URL + "v2/misc/";
  /** Blind updates poll often; team restrictions change rarely. */
  private static readonly GRIND_RESTRICTIONS_TTL_MS = 60_000;
  private grindRestrictionsMemory: {
    fetchedAt: number;
    data: unknown;
  } | null = null;
  private grindRestrictionsInFlight: Observable<unknown> | null = null;
  /**
   * Invalidation epoch for the in-memory grind-restrictions cache.
   * Save bumps it on start; forceRefresh bumps only when no save owns it.
   * Plain GETs only capture it so they cannot orphan a successful PUT.
   */
  private grindRestrictionsEpoch = 0;
  private grindRestrictionsInFlightEpoch = 0;
  private grindRestrictionsSaveEpoch: number | null = null;
  private grindRestrictionsSaveInFlight: Observable<unknown> | null = null;

  constructor(private http: HttpClient) {}

  searchTiers(name: string, page: number = 0, order?: Map<string, string>) {
    let params = {
      page: page.toString(),
      name,
    };

    if (order) {
      let mappedSortValues = new URLSearchParams();
      order.forEach((value: string, key: string) => {
        mappedSortValues.append(key, value);
      });

      params["order"] = mappedSortValues
        .toString()
        .replace(/[=]/g, ":")
        .replace(/[&]/g, ",");
    }
    return this.http.get<SearchTierResponse>(this.baseURL + "search", {
      params,
    }).pipe(
      withErrorHandler({
        operation: 'searchTiers',
        cache: true,
        fallback: {
        teams: [],
        pageCount: 0,
        page: 0,
        paging: { page: 0, per_page: 0, total: 0 },
        data: []
        }
      })
    );
  }

  getTier(id: number, admin: boolean = false) {
    const params = {};
    if (admin) {
      params["admin"] = "1";
    }
    return this.http.get<Tier>(this.baseURL + "get/" + id, {
      params,
    }).pipe(
      withErrorHandler({
        operation: 'getTier',
        cache: true,
        fallback: {} as Tier
      })
    );
  }

  getPlayerNote(userId: number) {
    return this.http.get<{ additional_info: string }>(this.baseURL + "player-note/" + userId).pipe(
      withErrorHandler({ operation: "getPlayerNote", timeoutMs: 15000, retries: 1 })
    );
  }

  putPlayerNote(userId: number, additionalInfo: string) {
    return this.http
      .put<{ additional_info: string }>(this.baseURL + "player-note/" + userId, {
        additional_info: additionalInfo,
      })
      .pipe(withErrorHandler({ operation: "putPlayerNote" }));
  }

  deleteTier(id: number, admin: boolean = false): Observable<boolean> {
    const params = {};
    if (admin) {
      params["admin"] = "1";
    }
    return this.http
      .delete(this.baseURL + "delete/" + id, {
        observe: "response",
        params,
      })
      .pipe(
        withErrorHandler({ operation: 'deleteTier', timeoutMs: 30000, retries: 1 }),
        catchError(err => {
          console.error('deleteTier failed:', err);
          return throwError(() => err);
        }),
        map((res) => res.status === 200)
      );
  }

  setUser(
    userId: number,
    tierId: number,
    master: boolean | null = null,
    admin: boolean = false
  ): Observable<boolean> {
    const params = {};
    if (admin) {
      params["admin"] = "1";
    }
    return this.http
      .post(
        this.baseURL + "user/add",
        {
          user: userId,
          tier: tierId,
          master,
        },
        {
          observe: "response",
          params,
        }
      )
      .pipe(
        withErrorHandler({ operation: 'setUser', timeoutMs: 30000, retries: 1 }),
        catchError(err => {
          console.error('setUser failed:', err);
          return throwError(() => err);
        }),
        map((res) => res.status === 200)
      );
  }

  setUserOnTiers(
    userId: number,
    tierIds: number[],
    master: boolean | null = null
  ): Observable<boolean> {
    return this.http
      .post(
        this.baseURL + "user/add",
        {
          user: userId,
          tiers: tierIds,
          master,
        },
        {
          observe: "response",
        }
      )
      .pipe(
        withErrorHandler({
          operation: "setUserOnTiers",
          timeoutMs: 30000,
          retries: 0,
        }),
        catchError((err) => {
          console.error("setUserOnTiers failed:", err);
          return throwError(err);
        }),
        map((res) => res.status === 200)
      );
  }

  setUserByEmail(
    userEmails: string | string[],
    master: boolean | null = null
  ): Observable<boolean> {
    const emailsArray = Array.isArray(userEmails) ? userEmails : [userEmails];
    const usersData = emailsArray
      .map((value) => value.trim())
      .filter((value) => value.length > 0)
      .map((value) => ({
        email: value,
        master,
      }));

    return this.http
      .post(
        this.baseURL + "user/add",
        {
          users: usersData,
        },
        {
          observe: "response",
        }
      )
      .pipe(
        withErrorHandler({ operation: 'setUserByEmail', timeoutMs: 30000, retries: 1 }),
        catchError(err => {
          console.error('setUserByEmail failed:', err);
          return throwError(() => err);
        }),
        map((res) => res.status === 200)
      );
  }

  removeUser(
    userIds: number | number[],
    tierId: number,
    admin: boolean = false
  ): Observable<boolean> {
    const params = {};
    if (admin) {
      params["admin"] = "1";
    }
    const idsArray = Array.isArray(userIds) ? userIds : [userIds];
    return this.http
      .post(
        this.baseURL + "user/remove",
        {
          users: idsArray,
          tier: tierId,
        },
        {
          observe: "response",
          params,
        }
      )
      .pipe(
        withErrorHandler({ operation: 'removeUser', timeoutMs: 30000, retries: 1 }),
        catchError(err => {
          console.error('removeUser failed:', err);
          return throwError(() => err);
        }),
        map((res) => res.status === 200)
      );
  }

  moveUsers(
    userIds: number | number[],
    fromTierId: number,
    toTierId: number,
    admin: boolean = false
  ): Observable<boolean> {
    const params = {};
    if (admin) {
      params["admin"] = "1";
    }
    const idsArray = Array.isArray(userIds) ? userIds : [userIds];
    return this.http
      .post(
        this.baseURL + "user/move",
        {
          users: idsArray,
          from_tier: fromTierId,
          to_tier: toTierId,
        },
        {
          observe: "response",
          params,
        }
      )
      .pipe(
        withErrorHandler({ operation: 'moveUsers', timeoutMs: 30000, retries: 1 }),
        catchError(err => {
          console.error('moveUsers failed:', err);
          return throwError(() => err);
        }),
        map((res) => res.status === 200)
      );
  }

  createTier(name: string, ownerID: number = 0, admin: boolean = false) {
    const params = {};
    if (admin) {
      params["admin"] = "1";
    }
    return this.http.post(
      this.baseURL + "create",
      {
        name,
        owner: ownerID,
      },
      {
        params,
      }
    ).pipe(
      withErrorHandler({ operation: 'createTier', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('createTier failed:', err);
        return throwError(() => err);
      })
    );
  }

  updateTier(
    id: number,
    body: {
      name?: string;
      use_own_license?: boolean;
      hide_nicks?: boolean;
    },
    admin: boolean = true
  ): Observable<boolean> {
    const params = {};
    if (admin) {
      params["admin"] = "1";
    }
    return this.http
      .put(this.baseURL + "update/" + id, body, {
        observe: "response",
        params,
      })
      .pipe(
        withErrorHandler({ operation: 'updateTier', timeoutMs: 30000, retries: 1 }),
        catchError(err => {
          console.error('updateTier failed:', err);
          return throwError(() => err);
        }),
        map((res) => res.status === 200)
      );
  }

  getTierLicense(tierId: number = 0, admin: boolean = false) {
    const params = {};
    if (admin) {
      params["tier"] = tierId.toString();
    }
    return this.http.get<TierLicense>(this.baseURL + "get-license", {
      params,
    }).pipe(
      withErrorHandler({
        operation: 'getTierLicense',
        cache: true,
        fallback: {} as TierLicense
      })
    );
  }

  copyTier(sourceId: number, targetId: string, copyType: string = "MERGE") {
    return this.http.post(this.baseURL + "copy-schedules", {
      source_tier: sourceId,
      target_tier: targetId,
      copy_type: copyType,
    }).pipe(
      withErrorHandler({ operation: 'copyTier', timeoutMs: 60000, retries: 1 }),
      catchError(err => {
        console.error('copyTier failed:', err);
        return throwError(() => err);
      })
    );
  }

  getRequest(master: boolean) {
    return this.http.get(this.baseURL + "request", {
      params: {
        master_query: master.toString(),
      },
    }).pipe(
      withErrorHandler({
        operation: 'getRequest',
        cache: true,
        fallback: []
      })
    );
  }

  putRequest(ids: Array<number>): Observable<boolean> {
    return this.http
      .put(
        this.baseURL + "request",
        {
          tournaments: ids,
        },
        {
          observe: "response",
        }
      )
      .pipe(
        withErrorHandler({ operation: 'putRequest', timeoutMs: 30000, retries: 1 }),
        catchError(err => {
          console.error('putRequest failed:', err);
          return throwError(() => err);
        }),
        map((res) => res.status === 200)
      );
  }

  manageRequest(
    tournaments: number[],
    user: number,
    accept: boolean,
    tier: number,
    replicate: boolean,
    all_members: boolean
  ): Observable<boolean> {
    return this.http
      .put(
        this.baseURL + "request/manage",
        {
          tournaments,
          user,
          accept,
          tier,
          replicate,
          all_members,
        },
        {
          observe: "response",
        }
      )
      .pipe(
        withErrorHandler({ operation: 'manageRequest', timeoutMs: 30000, retries: 1 }),
        catchError(err => {
          console.error('manageRequest failed:', err);
          return throwError(() => err);
        }),
        map((res) => res.status === 200)
      );
  }

  listRequest(): Observable<any> {
    return this.http.get(this.baseURL + "request/list").pipe(
      withErrorHandler({ operation: 'listRequest', cache: true, fallback: [] })
    );
  }

  getLogs(body): Observable<any> {
    return this.http.post<any>(this.baseURL + "log", body).pipe(
      withErrorHandler({ operation: 'getLogs', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('getLogs failed:', err);
        return of([]);
      })
    );
  }

  getTierInfo(): Observable<any> {
    return this.http.get<any>(this.baseURL + "info").pipe(
      withErrorHandler({ operation: 'getTierInfo', cache: true, fallback: {} })
    );
  }

  putTierInfo(body): Observable<any> {
    return this.http.put<any>(this.baseURL + "info", body).pipe(
      withErrorHandler({ operation: 'putTierInfo', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('putTierInfo failed:', err);
        return throwError(() => err);
      })
    );
  }

  getRedFlag(): Observable<any> {
    return this.http.get<any>(this.baseURL + "flag").pipe(
      withErrorHandler({ operation: 'getRedFlag', cache: true, fallback: {} })
    );
  }

  putTierFlag(body): Observable<any> {
    return this.http.put<any>(this.baseURL + "flag", body).pipe(
      withErrorHandler({ operation: 'putTierFlag', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('putTierFlag failed:', err);
        return throwError(() => err);
      })
    );
  }

  getRebalanceSettings(): Observable<any> {
    return this.http.get<any>(this.baseURL + "as-settings").pipe(
      withErrorHandler({ operation: 'getRebalanceSettings', cache: true, fallback: {} })
    );
  }

  saveRebalanceSettings(body): Observable<any> {
    return this.http.put<any>(this.baseURL + "as-settings", body).pipe(
      withErrorHandler({ operation: 'saveRebalanceSettings', timeoutMs: 30000, retries: 1 }),
      catchError(err => {
        console.error('saveRebalanceSettings failed:', err);
        return throwError(() => err);
      })
    );
  }

  getGrindRestrictions(options?: {
    forceRefresh?: boolean;
  }): Observable<unknown> {
    const forceRefresh = options?.forceRefresh === true;
    if (forceRefresh && this.grindRestrictionsSaveInFlight) {
      const save$ = this.grindRestrictionsSaveInFlight;
      return save$.pipe(
        catchError(() => {
          if (this.grindRestrictionsSaveInFlight === save$) {
            this.grindRestrictionsSaveInFlight = null;
          }
          this.grindRestrictionsSaveEpoch = null;
          return this.getGrindRestrictions({ forceRefresh: true });
        })
      );
    }
    const cached = this.grindRestrictionsMemory;
    if (
      !forceRefresh &&
      cached &&
      Date.now() - cached.fetchedAt < TierService.GRIND_RESTRICTIONS_TTL_MS
    ) {
      return of(cached.data);
    }
    if (!forceRefresh && this.grindRestrictionsInFlight) {
      return this.grindRestrictionsInFlight;
    }

    if (forceRefresh) {
      const nextEpoch = nextGrindRestrictionsForceRefreshEpoch(
        this.grindRestrictionsEpoch,
        this.grindRestrictionsSaveEpoch
      );
      if (nextEpoch !== this.grindRestrictionsEpoch) {
        this.grindRestrictionsEpoch = nextEpoch;
        this.grindRestrictionsMemory = null;
        this.grindRestrictionsInFlight = null;
      }
    }

    // Capture only — do not bump. Bumping here used to orphan an in-flight
    // save whose tap still held the previous generation.
    const requestEpoch = this.grindRestrictionsEpoch;
    const request$ = this.http
      .get<unknown>(this.baseURL + "grind-restrictions")
      .pipe(
        withErrorHandler({
          operation: "getGrindRestrictions",
          timeoutMs: 30000,
          retries: 1,
        }),
        tap((data) => {
          if (
            !shouldCommitGrindRestrictionsCache(
              requestEpoch,
              this.grindRestrictionsEpoch
            )
          ) {
            return;
          }
          this.grindRestrictionsMemory = {
            fetchedAt: Date.now(),
            data,
          };
        }),
        catchError((err) => {
          console.error("getGrindRestrictions failed:", err);
          return throwError(() => err);
        }),
        finalize(() => {
          if (
            shouldCommitGrindRestrictionsCache(
              requestEpoch,
              this.grindRestrictionsInFlightEpoch
            )
          ) {
            this.grindRestrictionsInFlight = null;
          }
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );

    this.grindRestrictionsInFlight = request$;
    this.grindRestrictionsInFlightEpoch = requestEpoch;
    return request$;
  }

  saveGrindRestrictions(
    restrictions: GrindRestriction[]
  ): Observable<unknown> {
    const body = {
      restrictions: restrictions.map((row) => ({
        variant: row.variant,
        speed: row.speed,
        min_current_blind: row.min_current_blind,
      })),
    };
    // Invalidate in-flight GETs so a late response cannot replace the save.
    const saveEpoch = ++this.grindRestrictionsEpoch;
    this.grindRestrictionsSaveEpoch = saveEpoch;
    this.grindRestrictionsInFlight = null;
    this.grindRestrictionsInFlightEpoch = saveEpoch;
    let request$: Observable<unknown>;
    request$ = this.http
      .put<unknown>(this.baseURL + "grind-restrictions", body)
      .pipe(
        withErrorHandler({
          operation: "saveGrindRestrictions",
          timeoutMs: 30000,
          retries: 1,
        }),
        map((data) => {
          const hasRestrictions =
            data != null &&
            typeof data === "object" &&
            Array.isArray((data as { restrictions?: unknown }).restrictions);
          return hasRestrictions ? data : body;
        }),
        tap((payload) => {
          if (
            !shouldCommitGrindRestrictionsCache(
              saveEpoch,
              this.grindRestrictionsEpoch
            )
          ) {
            return;
          }
          this.grindRestrictionsMemory = {
            fetchedAt: Date.now(),
            data: payload,
          };
          // Advance past any GET that captured saveEpoch while the PUT was
          // in flight, so it cannot overwrite this write with pre-save data.
          this.grindRestrictionsEpoch++;
          this.grindRestrictionsSaveEpoch = null;
        }),
        catchError((err) => {
          console.error("saveGrindRestrictions failed:", err);
          if (this.grindRestrictionsSaveEpoch === saveEpoch) {
            this.grindRestrictionsSaveEpoch = null;
          }
          return throwError(() => err);
        }),
        finalize(() => {
          if (this.grindRestrictionsSaveInFlight === request$) {
            this.grindRestrictionsSaveInFlight = null;
          }
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    this.grindRestrictionsSaveInFlight = request$;
    return request$;
  }

  saveRebalanceSettingsBulk(
    tiers: number[],
    policy: {
      auto_exclude: boolean;
      keep_user_tournaments: boolean;
      exclude_thresholds: {
        prize_pool_threshold: number | null;
        field_threshold: number | null;
      };
    }
  ): Observable<{ failed: number[] }> {
    return this.http
      .put<{ failed: number[] }>(this.baseURL + "as-settings/bulk", {
        tiers,
        ...policy,
      })
      .pipe(
        withErrorHandler({
          operation: "saveRebalanceSettingsBulk",
          timeoutMs: 60000,
          retries: 1,
        }),
        catchError((err) => {
          console.error("saveRebalanceSettingsBulk failed:", err);
          return throwError(() => err);
        })
      );
  }

  executeAutoScheduler(): Observable<any> {
    return this.http
      .post<any>(
        this.baseURLMisc + "auto_schedule/tier",
        {},
        {
          observe: "response",
        }
      )
      .pipe(
        withErrorHandler({ operation: 'executeAutoScheduler', timeoutMs: 60000, retries: 1 }),
        catchError(err => {
          console.error('executeAutoScheduler failed:', err);
          return throwError(() => err);
        }),
        tap(() => {
          localStorage.setItem("refresh-schedules", "true");
        }),
        map((res) => res.status === 200)
      );
  }

  leaveTier(tier: number) {
    return this.http
      .post(this.baseURL + "user/leave", {
        tier: tier,
      })
      .pipe(
        withErrorHandler({ operation: 'leaveTier', timeoutMs: 30000, retries: 1 }),
        catchError(err => {
          console.error('leaveTier failed:', err);
          return throwError(() => err);
        })
      )
      .toPromise();
  }
  
  resetSchedules(id, body): Observable<any> {
    return this.http.post<any>(this.baseURL + "reset/" + id, body).pipe(
      withErrorHandler({ operation: 'resetSchedules', timeoutMs: 60000, retries: 1 }),
      catchError(err => {
        console.error('resetSchedules failed:', err);
        return throwError(() => err);
      }),
      tap(() => {
        localStorage.setItem("refresh-schedules", "true");
      })
    );
  }

}
